<template>
  <div>
    <div v-if="loading">
      <LoadingSpinner :full="true" />
    </div>

    <div v-else>
      <div class="main-grid" :class="{ 'main-grid--no-fill-data': !shouldDisplayChart }">
        <div class="content-wrap fill-level" v-if="shouldDisplayChart">
          <header class="content-header cf">
            <h3>{{ $t('containers.fillLevelOverview') }}</h3>
          </header>
          <div class="content-body chart-wrapper">
            <ContainerValuesChart
              v-if="loading === false"
              :chartManualValues="chartManualValues"
              :chartValues="chartValues"
              :emptyingDates="chartEmptyDates"
            />
            <div v-else>
              <LoadingSpinner />
            </div>
          </div>
        </div>
        <div class="content-wrap base-data">
          <ContainerBaseData :container="container" />
        </div>
        <div class="content-wrap map">
          <header class="content-header cf">
            <h3>{{ $t('containers.locationOverview') }}</h3>
          </header>
          <div class="content-body">
            <ContainerMap :container="container" :height="'26rem'" :extraMarkers="details.location_values.data" />
          </div>
        </div>
        <div class="content-wrap details">
          <header class="content-header cf">
            <h3>{{ $t('containers.sensorValues') }}</h3>
            <div v-if="container.sensors && container.sensors.length" class="header-meta">
              <p>{{ $t('containers.sensors') }}:</p>
              <p v-for="sensor in container.sensors" :key="sensor.uuid">
                <Link :to="`/company/sensors/${sensor.uuid}`">{{ sensor.hardware_id }}</Link>
              </p>
            </div>
          </header>
          <div class="content-body">
            <Tabs
              :tabList="[
                { key: 'values', name: $t('containers.values'), disabled: !details.values.data.length },
                {
                  key: 'manualValues',
                  name: $t('containers.manualValues'),
                  disabled: !details.manual_values.data.length,
                },
                { key: 'emptyDates', name: $t('containers.emptyDates'), disabled: !details.empty_dates.data.length },
                { key: 'batteryLevels', name: $t('containers.battery'), disabled: !details.battery_levels.data.length },
                {
                  key: 'signalQualities',
                  name: $t('containers.signalQuality'),
                  disabled: !details.signal_qualities.data.length,
                },
                { key: 'orders', name: $t('meta.orders'), disabled: !details.orders.data.length },
                {
                  key: 'weighAndEmpty',
                  name: $t('containers.weighAndEmpty'),
                  disabled: !details.weigh_and_empty_orders.data.length,
                },
                {
                  key: 'locationValues',
                  name: $t('containers.locationHistory'),
                  disabled: !details.location_values.data.length,
                },
                { key: 'uvvChecks', name: $t('containers.uvvChecks'), disabled: !details.uvv_checks.data.length },
              ]"
            >
              <template #tab-0>
                <DataTable
                  :values="details.values.data"
                  :maxTableLength="maxTableLength"
                  :rows="[
                    { name: $t('containers.date'), attribute: value => formattedFromDateTime(value.measured_at) },
                    { name: $t('containers.type'), attribute: () => $t('containers.fillLevel') },
                    { name: $t('containers.value'), attribute: value => Math.floor(value.fill_level * 100) },
                  ]"
                />
              </template>
              <template #tab-1>
                <DataTable
                  :values="details.manual_values.data"
                  :maxTableLength="maxTableLength"
                  :rows="[
                    { name: $t('containers.date'), attribute: value => formattedFromDateTime(value.measured_at) },
                    { name: $t('containers.type'), attribute: () => $t('containers.fillLevel') },
                    { name: $t('containers.value'), attribute: value => Math.floor(value.fill_level * 100) },
                  ]"
                />
              </template>
              <template #tab-2>
                <DataTable
                  :values="details.empty_dates.data"
                  :maxTableLength="maxTableLength"
                  :totalValuesCount="details.empty_dates.total"
                  :rows="[
                    { name: $t('containers.date'), attribute: value => formattedFromDateTime(value.measured_at) },
                    { name: $t('containers.type'), attribute: () => $t('containers.emptyDate') },
                    { name: $t('containers.value'), attribute: value => $t('containers.emptying') },
                  ]"
                />
              </template>
              <template #tab-3>
                <DataTable
                  :values="details.battery_levels.data"
                  :maxTableLength="maxTableLength"
                  :totalValuesCount="details.battery_levels.total"
                  :rows="[
                    { name: $t('containers.date'), attribute: value => formattedFromDateTime(value.measured_at) },
                    { name: $t('containers.type'), attribute: () => $t('containers.battery') },
                    { name: $t('containers.value'), attribute: value => Math.floor(value.percentage * 100) },
                    { name: $t('containers.sensor'), attribute: value => value.hardware_id },
                  ]"
                />
              </template>
              <template #tab-4>
                <DataTable
                  :values="details.signal_qualities.data"
                  :maxTableLength="maxTableLength"
                  :totalValuesCount="details.signal_qualities.total"
                  :rows="[
                    { name: $t('containers.date'), attribute: value => formattedFromDateTime(value.measured_at) },
                    { name: $t('containers.type'), attribute: () => $t('containers.signalQuality') },
                    { name: $t('containers.value'), attribute: value => Math.floor(value.percentage * 100) },
                    { name: $t('containers.sensor'), attribute: value => value.hardware_id },
                  ]"
                />
              </template>
              <template #tab-5>
                <DataTable
                  :values="details.orders.data"
                  :maxTableLength="maxTableLength"
                  :totalValuesCount="details.orders.total"
                  :rows="[
                    { name: $t('meta.date'), attribute: value => formattedFromDateTime(value.updated_at) },
                    { name: $t('meta.orderType'), slot: 'type' },
                    { name: $t('meta.area'), attribute: value => value.area_name },
                    { name: $t('meta.site'), attribute: value => value.site_name },
                    { name: $t('meta.action'), attribute: value => value.actions.join(', ') },
                  ]"
                >
                  <template #type="{ value }">
                    <Link :to="`/orders/${value.uuid}?companyUuid=${container.company.uuid}&status=all&period=all`">{{
                      value.human_type
                    }}</Link>
                  </template>
                </DataTable>
              </template>
              <template #tab-6>
                <DataTable
                  :values="details.weigh_and_empty_orders.data"
                  :maxTableLength="maxTableLength"
                  :totalValuesCount="details.weigh_and_empty_orders.count"
                  :rows="[
                    { name: $t('meta.date'), attribute: value => formattedFromDateTime(value.driver_finished_at) },
                    { name: $t('meta.orderType'), slot: 'type' },
                    { name: $t('meta.weight'), slot: 'weight' },
                    { name: $t('meta.fraction'), attribute: value => value.fraction },
                  ]"
                >
                  <template #type="{ value }">
                    <Link :to="`/orders/${value.uuid}?companyUuid=${container.company.uuid}&status=all&period=all`">{{
                      value.human_type
                    }}</Link>
                  </template>
                  <template #weight="{ value }">
                    <span class="numeric">{{ value.material_weight_kg }}</span>
                    <span v-if="ORDER_TYPE.OVERHEAD_LOADING === value.type">{{ $t('containers.weightAveraged') }}</span>
                  </template>
                </DataTable>
              </template>
              <template #tab-7>
                <DataTable
                  :values="details.location_values.data"
                  :subtitle="$t('containers.locationHistorySubtitle')"
                  :maxTableLength="maxTableLength"
                  :totalValuesCount="details.location_values.total"
                  :rows="[
                    { name: $t('containers.date'), attribute: value => formattedFromDateTime(value.measured_at) },
                    { name: $t('routes.lat'), attribute: value => value.lat },
                    { name: $t('routes.lng'), attribute: value => value.lng },
                    { name: $t('containers.accuracy'), attribute: value => value.accuracy },
                  ]"
                />
              </template>
              <template #tab-8>
                <DataTable
                  :values="details.uvv_checks.data"
                  :maxTableLength="maxTableLength"
                  :totalValuesCount="details.uvv_checks.total"
                  :rows="[
                    {
                      name: $t('containers.uvvCheckPerformedAt'),
                      attribute: value => formattedDate(value.performed_at),
                    },
                    { name: $t('containers.uvvCheckBadge'), slot: 'badge' },
                    { name: $t('containers.uvvCheckReport'), slot: 'report' },
                    { name: $t('containers.uvvCheckDueAt'), attribute: value => formattedDate(value.date) },
                  ]"
                >
                  <template #badge="{ value }">
                    <ImageComponent
                      v-if="value.badge"
                      :preview="value.badge.preview_url"
                      :src="value.badge.url"
                      :alt="$t('containers.uvvCheckBadge')"
                      :height="'100'"
                    />
                  </template>
                  <template #report="{ value }">
                    <a v-if="value.doc" :href="value.doc" target="_blank" class="items-center">
                      <span>{{ $t('containers.downloadPdf') }}</span>
                      <i class="material-icons">chevron_right</i>
                    </a>
                    <span v-else>–</span>
                  </template>
                </DataTable>
              </template>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DataTable from '@/components/DataTable'
import ImageComponent from '@/components/Image.vue'
import LoadingSpinner from '@/components/LoadingSpinner'
import Tabs from '@/components/Tabs'
import ContainerBaseData from '@/components/container/BaseData'
import ContainerMap from '@/components/container/ContainerMap'
import ContainerValuesChart from '@/components/container/ContainerValuesChart'
import dateUtils from '@/components/mixins/dateUtils'

import containersApi from '@/api/container'
import { ORDER_TYPE } from '@/api/orders'
import userApi from '@/api/users'

const chartDataMapper = ({ measured_at, fill_level }) => [measured_at, Math.floor(fill_level * 100)]
export default {
  props: ['containerUuid'],
  components: {
    LoadingSpinner,
    ContainerBaseData,
    ContainerValuesChart,
    ContainerMap,
    DataTable,
    Tabs,
    ImageComponent,
  },
  data() {
    return {
      ORDER_TYPE,
      details: {},
      container: null,
      loading: true,
      maxTableLength: 25,
      user: null,
    }
  },
  mixins: [dateUtils],
  computed: {
    primarySensor() {
      return this.container.sensors?.[0]
    },
    chartManualValues() {
      return this.details.manual_values.data.map(chartDataMapper)
    },
    chartValues() {
      return this.details.values.data.map(chartDataMapper)
    },
    chartEmptyDates() {
      return this.details.empty_dates.data.map(x => [x.measured_at, 0])
    },
    shouldDisplayChart() {
      return this.chartManualValues?.length || this.chartValues?.length || this.chartEmptyDates?.length
    },
  },
  async mounted() {
    // all requests that can happen in parallel
    await Promise.all([
      containersApi.get(this.containerUuid).then(result => (this.container = result)),
      userApi.getCurrent().then(result => (this.user = result)),
      containersApi.getDetails(this.containerUuid).then(result => (this.details = result)),
    ])

    this.loading = false
  },
}
</script>

<style lang="scss">
.numeric {
  font-variant-numeric: tabular-nums;
  padding-right: 6px;
}
.actions {
  .comma-separated:not(:last-of-type)::after {
    content: ', ';
  }
}
</style>

<style lang="scss" scoped>
.main-grid {
  display: grid;
  grid-template-columns: 5fr 5fr;
  grid-template-rows: auto auto auto;
  gap: 1.25rem;
  grid-template-areas:
    'base-data base-data'
    'map fill-level'
    'details details';
  margin: 1.25rem 1.25rem 0;
  padding-bottom: 2rem;

  &--no-fill-data {
    grid-template-areas:
      'base-data'
      'map'
      'details';
    grid-template-columns: 1fr;
  }

  .content-wrap {
    margin: 0;
  }

  .header-meta {
    p {
      display: inline-block;
      padding: 9px;

      a {
        color: #444;
      }
    }

    p:first-child {
      padding-right: 0;
    }
  }
}

.no-data {
  color: #a8a8a8;
  text-align: center;
  align-self: center;

  p {
    margin-top: 2rem;
  }
}

.fill-level {
  grid-area: fill-level;

  .chart-wrapper {
    display: inline-grid;
  }
}

.base-data {
  grid-area: base-data;
  height: max-content;
}

.details {
  grid-area: details;
}

.map {
  grid-area: map;
}

.subtitle {
  padding-bottom: 0;
  font-size: 0.875rem;
  font-weight: 500;
}
</style>
